import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

type AccountBannerScheme = 'family' | 'coach' | 'host';

const getAccountTypeBanner = (accountType?: string): {
  label: string;
  icon: string;
  scheme: AccountBannerScheme;
} => {
  const normalizedType = String(accountType || '').trim().toLowerCase();
  switch (normalizedType) {
    case 'host':
      return { label: 'Host', icon: 'storefront-outline', scheme: 'host' };
    case 'coach':
      return { label: 'Coach', icon: 'whistle', scheme: 'coach' };
    case 'parent':
    case 'family':
    case 'user':
    default:
      return { label: 'Parent', icon: 'account-heart-outline', scheme: 'family' };
  }
};

interface AccountTypeBadgeProps {
  accountType?: string;
  compact?: boolean;
}

const AccountTypeBadge: React.FC<AccountTypeBadgeProps> = ({ accountType, compact = true }) => {
  const banner = getAccountTypeBanner(accountType);

  return (
    <View
      style={[
        styles.badge,
        compact ? styles.badgeCompact : styles.badgeRegular,
        banner.scheme === 'family' && styles.badgeFamily,
        banner.scheme === 'coach' && styles.badgeCoach,
        banner.scheme === 'host' && styles.badgeHost,
      ]}
      accessibilityRole="text"
      accessibilityLabel={`Account type: ${banner.label}`}
    >
      <MaterialCommunityIcons
        name={banner.icon as any}
        size={compact ? 15 : 16}
        color={
          banner.scheme === 'family'
            ? '#2563EB'
            : banner.scheme === 'coach'
              ? '#059669'
              : '#EA580C'
        }
      />
      <Text
        style={[
          styles.badgeText,
          compact ? styles.badgeTextCompact : styles.badgeTextRegular,
          banner.scheme === 'family' && styles.badgeTextFamily,
          banner.scheme === 'coach' && styles.badgeTextCoach,
          banner.scheme === 'host' && styles.badgeTextHost,
        ]}
        numberOfLines={1}
      >
        {banner.label}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    borderRadius: 999,
    borderWidth: 1,
  },
  badgeCompact: {
    paddingVertical: 5,
    paddingLeft: 8,
    paddingRight: 12,
    maxWidth: 140,
  },
  badgeRegular: {
    paddingVertical: 6,
    paddingLeft: 10,
    paddingRight: 14,
    maxWidth: 180,
  },
  badgeFamily: {
    backgroundColor: '#EEF6FF',
    borderColor: '#BFDBFE',
  },
  badgeCoach: {
    backgroundColor: '#ECFDF5',
    borderColor: '#A7F3D0',
  },
  badgeHost: {
    backgroundColor: '#FFF7ED',
    borderColor: '#FCD34D',
  },
  badgeText: {
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
  },
  badgeTextCompact: {
    fontSize: 11,
  },
  badgeTextRegular: {
    fontSize: 12,
  },
  badgeTextFamily: { color: '#1D4ED8' },
  badgeTextCoach: { color: '#047857' },
  badgeTextHost: { color: '#C2410C' },
});

export default AccountTypeBadge;

